import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import loginBg from "../../assets/illustrations/login-bg.jfif";
import { T } from "../../constants/theme";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";

export default function AuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const { login, adminLogin, register } = useContext(AuthContext);
  const toast = useContext(ToastContext);

  const isAdminMode = mode === "admin-login";
  const isLogin = mode !== "register";
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    newsletter: false,
    role: "customer",
  });

  const [errors, setErrors] = useState({});

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  // ---------------- HELPERS ----------------
  const isEmpty = (v) => !v || v.trim() === "";

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const passwordStrong = (p) =>
    p.length >= 10 &&
    /[A-Z]/.test(p) &&
    /[a-z]/.test(p) &&
    /\d/.test(p) &&
    /[^A-Za-z0-9]/.test(p);

  const mapError = (msg) => {
    const m = msg?.toLowerCase() || "";
    if (m.includes("email already")) return "Email already exists";
    if (m.includes("invalid credentials")) return "Wrong email or password";
    if (m.includes("not found")) return "Account not found";
    if (m.includes("disabled")) return "Account disabled";
    return msg || "Something went wrong";
  };

  // ---------------- VALIDATION ----------------
  const validate = () => {
    const err = {};

    if (isLogin || isAdminMode) {
      if (isEmpty(form.email)) err.email = "Email required";
      else if (!isValidEmail(form.email.trim())) err.email = "Invalid email";

      if (isEmpty(form.password)) err.password = "Password required";
    }

    if (!isLogin && !isAdminMode) {
      if (isEmpty(form.first_name)) err.first_name = "Required";
      if (isEmpty(form.last_name)) err.last_name = "Required";

      if (isEmpty(form.email)) err.email = "Email required";
      else if (!isValidEmail(form.email.trim())) err.email = "Invalid email";

      if (isEmpty(form.password))
        err.password = "Password required";
      else if (!passwordStrong(form.password))
        err.password = "Weak password";
    }

    return err;
  };

  // ---------------- SUBMIT ----------------
  const submit = async () => {
    const v = validate();
    setErrors(v);

    if (Object.keys(v).length > 0) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }

    setLoading(true);

    const email = form.email.trim();

    try {
      if (isLogin && isAdminMode) {
        await adminLogin(email, form.password);
      } else if (isLogin) {
        await login(email, form.password);
      } else {
        await register({ ...form, email });
      }

      toast(isAdminMode ? "Admin login success" : "Welcome 🌱");
      navigate(isAdminMode ? "/admin" : "/home");
    } catch (e) {
      const msg = mapError(e.message);
      setErrors({ form: msg });
      setShake(true);
      setTimeout(() => setShake(false), 400);
      toast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- STYLES ----------------
  const inputStyle = (field) => ({
    width: "100%",
    padding: "14px",
    borderRadius: 12,
    border: errors[field]
      ? "1px solid #ff6b6b"
      : `1px solid ${T.border}`,
    background: "rgba(255,255,255,0.08)",
    color: T.ink,
    fontSize: 14,
    outline: "none",
    marginBottom: 10,
  });

  const btnPrimary = {
    width: "100%",
    marginTop: 14,
    padding: 14,
    borderRadius: 12,
    border: "none",
    background: T.gold,
    color: T.paper,
    fontWeight: 700,
    cursor: "pointer",
  };

  const btnLink = {
    border: "none",
    background: "transparent",
    color: T.ink,
    fontWeight: 700,
    cursor: "pointer",
  };

  // ---------------- UI ----------------
return (
  <div
    style={{
      minHeight: "100vh",
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "40px 20px",
      backgroundImage: `url(${loginBg})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      overflow: "hidden",
    }}
  >
    {/* Overlay */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45))",
      }}
    />

    {/* Styles */}
    <style>
      {`
        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          50% { transform: translateX(6px); }
          75% { transform: translateX(-4px); }
          100% { transform: translateX(0); }
        }

        .auth-card{
          width:100%;
          max-width:520px;
        }

        .name-grid{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:14px;
        }

        .auth-input::placeholder{
          color:${T.muted};
        }

        @media(max-width:768px){

          .auth-card{
            max-width:95%;
            padding:24px !important;
          }

          .name-grid{
            grid-template-columns:1fr;
          }

        }
      `}
    </style>

    {/* Back Button */}
    <button
      onClick={() => navigate("/shop")}
      style={{
        position: "absolute",
        top: 25,
        left: 25,
        zIndex: 10,
        border: "none",
        padding: "12px 18px",
        borderRadius: 12,
        background: "rgba(255,255,255,.15)",
        backdropFilter: "blur(10px)",
        color: T.ink,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      ← Back to Shop
    </button>

    {/* Card */}
    <div
      className="auth-card"
      style={{
        position: "relative",
        zIndex: 5,
        padding: 40,
        borderRadius: 24,
        background: T.surface,
        backdropFilter: "blur(20px)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.05)",
        border: `1px solid ${T.border}`,
        animation: shake ? "shake .4s ease" : "none",
      }}
    >
      {/* Brand */}
      <div
        style={{
          textAlign: "center",
          marginBottom: 28,
        }}
      >
        <div
          style={{
            fontSize: 12,
            letterSpacing: 4,
            fontWeight: 700,
            color: T.muted,
            marginBottom: 10,
          }}
        >
          Greenbless : Rooted in Quality
        </div>

        <h1
          style={{
            margin: 0,
            fontSize: 34,
            color: T.ink,
          }}
        >
          {isAdminMode
            ? "Admin Access"
            : isLogin
            ? "Welcome Back"
            : "Create Account"}
        </h1>

        <p
          style={{
            marginTop: 12,
            color: T.muted,
            fontSize: 15,
            lineHeight: 1.6,
          }}
        >
          {isLogin
            ? "Sign in to access your account and continue shopping."
            : "Create your account and enjoy fresh produce, seeds, and premium tools."}
        </p>
      </div>

      {/* Global Error */}
      {errors.form && (
        <div
          style={{
            background: "rgba(255,107,107,0.12)",
            border: "1px solid rgba(255,107,107,0.3)",
            color: "#FF6B6B",
            padding: 14,
            borderRadius: 12,
            marginBottom: 20,
            fontSize: 14,
          }}
        >
          {errors.form}
        </div>
      )}

      {/* Account Type */}
      {!isLogin && !isAdminMode && (
        <div
          className="name-grid"
          style={{ marginBottom: 14 }}
        >
          {[
            { value: "customer", label: "Investor" },
            { value: "farmer", label: "Farmer" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, role: opt.value }))}
              style={{
                padding: "14px",
                borderRadius: 12,
                border:
                  form.role === opt.value
                    ? `2px solid ${T.gold}`
                    : `1px solid ${T.border}`,
                background:
                  form.role === opt.value ? T.goldLight : T.paper,
                color: form.role === opt.value ? T.gold : T.ink,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Register Fields */}
      {!isLogin && !isAdminMode && (
        <div
          className="name-grid"
          style={{ marginBottom: 14 }}
        >
          <div>
            <input
              className="auth-input"
              style={{
                ...inputStyle("first_name"),
                background: T.paper,
                color: T.ink,
                border:
                  errors.first_name
                    ? "1px solid #ff4d4f"
                    : `1px solid ${T.border}`,
              }}
              placeholder="First Name"
              value={form.first_name}
              onChange={set("first_name")}
            />
          </div>

          <div>
            <input
              className="auth-input"
              style={{
                ...inputStyle("last_name"),
                background: T.paper,
                color: T.ink,
                border:
                  errors.last_name
                    ? "1px solid #ff4d4f"
                    : `1px solid ${T.border}`,
              }}
              placeholder="Last Name"
              value={form.last_name}
              onChange={set("last_name")}
            />
          </div>
        </div>
      )}

      {/* Email */}
      <div style={{ marginBottom: 16 }}>
        <input
          className="auth-input"
          style={{
            ...inputStyle("email"),
            background: T.paper,
            color: T.ink,
            border:
              errors.email
                ? "1px solid #ff4d4f"
                : `1px solid ${T.border}`,
          }}
          placeholder="Email Address"
          value={form.email}
          onChange={set("email")}
        />

        {errors.email && (
          <div
            style={{
              color: "#e53935",
              marginTop: 6,
              fontSize: 13,
            }}
          >
            {errors.email}
          </div>
        )}
      </div>

      {/* Password */}
      <div
        style={{
          position: "relative",
          marginBottom: 16,
        }}
      >
        <input
          className="auth-input"
          type={showPassword ? "text" : "password"}
          style={{
            ...inputStyle("password"),
            background: T.paper,
            color: T.ink,
            border:
              errors.password
                ? "1px solid #ff4d4f"
                : `1px solid ${T.border}`,
          }}
          placeholder="Password"
          value={form.password}
          onChange={set("password")}
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          style={{
            position: "absolute",
            right: 15,
            top: 15,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: T.muted,
            fontWeight: 600,
          }}
        >
          {showPassword ? "Hide" : "Show"}
        </button>

        {errors.password && (
          <div
            style={{
              color: "#e53935",
              marginTop: 6,
              fontSize: 13,
            }}
          >
            {errors.password}
          </div>
        )}
      </div>

      {/* Forgot Password */}
      {isLogin && (
        <div style={{ textAlign: "right", marginBottom: 16, marginTop: -8 }}>
          <button
            type="button"
            onClick={() =>
              navigate(
                isAdminMode ? "/forgot-password?admin=1" : "/forgot-password"
              )
            }
            style={{
              border: "none",
              background: "none",
              color: T.gold,
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Forgot password?
          </button>
        </div>
      )}

      {/* Newsletter */}
      {!isLogin && !isAdminMode && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 20,
            color: T.muted,
            fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={form.newsletter}
            onChange={set("newsletter")}
          />
          Receive exclusive offers and updates
        </label>
      )}

      {/* Submit */}
      <button
        onClick={submit}
        disabled={loading}
        style={{
          width: "100%",
          height: 56,
          borderRadius: 16,
          border: "none",
          background:
            `linear-gradient(135deg, ${T.gold}, ${T.goldHover})`,
          color: T.paper,
          fontWeight: 700,
          fontSize: 15,
          cursor: "pointer",
          boxShadow:
            "0 12px 32px rgba(0,0,0,0.05)",
        }}
      >
        {loading
          ? "Processing..."
          : isLogin
          ? "Sign In"
          : "Create Account"}
      </button>

      {/* Switch */}
      <div
        style={{
          textAlign: "center",
          marginTop: 20,
          color: T.muted,
        }}
      >
        {isLogin
          ? "Don't have an account?"
          : "Already have an account?"}

        <button
          onClick={() => navigate(isLogin ? "/register" : "/login")}
          style={{
            marginLeft: 8,
            border: "none",
            background: "none",
            color: T.ink,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {isLogin ? "Register" : "Login"}
        </button>
      </div>
    </div>
  </div>
);
}
